import { useEffect, useState } from 'react'

interface AdminUser {
  username: string
  role: 'admin' | 'user'
  credits: number
  usedCalls: number
  createdAt: string
}

interface Invite {
  code: string
  createdAt: string
  usedBy: string | null
  usedAt: string | null
}

/** 관리자 전용: 사용자 목록 + 캐시 지급/차감 */
export default function AdminDialog({ onClose }: { onClose: () => void }) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [error, setError] = useState('')
  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    try {
      const [uRes, iRes] = await Promise.all([fetch('/api/admin/users'), fetch('/api/admin/invites')])
      const uData = await uRes.json()
      const iData = await iRes.json()
      if (!uRes.ok) throw new Error(uData.error || `오류 ${uRes.status}`)
      setUsers(uData.users ?? [])
      setInvites(iData.invites ?? [])
      setError('')
    } catch (e: any) {
      setError(e?.message ?? String(e))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  const grant = async (username: string, sign: 1 | -1) => {
    const raw = amounts[username] ?? '10'
    const delta = sign * Math.trunc(Number(raw))
    if (!delta) return setError('지급량은 0이 아닌 정수여야 합니다.')
    setBusy(username)
    try {
      const res = await fetch('/api/admin/credits', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ username, delta }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `오류 ${res.status}`)
      await load()
    } catch (e: any) {
      setError(e?.message ?? String(e))
    } finally {
      setBusy('')
    }
  }

  const issueInvite = async () => {
    setBusy('__invite__')
    try {
      const res = await fetch('/api/admin/invites', { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `오류 ${res.status}`)
      setNotice(`새 초대코드: ${data.code}`)
      setError('')
      await load()
    } catch (e: any) {
      setError(e?.message ?? String(e))
    } finally {
      setBusy('')
    }
  }

  const revoke = async (code: string) => {
    setBusy(code)
    try {
      const res = await fetch('/api/admin/invites/revoke', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ code }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `오류 ${res.status}`)
      await load()
    } catch (e: any) {
      setError(e?.message ?? String(e))
    } finally {
      setBusy('')
    }
  }

  const resetPassword = async (username: string) => {
    setBusy(username)
    setNotice('')
    try {
      const res = await fetch('/api/admin/password', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ username }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || `오류 ${res.status}`)
      setNotice(`${username} 임시 비밀번호: ${data.temp} — 본인에게 전달하고 로그인 후 변경하게 하세요.`)
      setError('')
    } catch (e: any) {
      setError(e?.message ?? String(e))
    } finally {
      setBusy('')
    }
  }

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal admin-modal">
        <div className="modal-head">
          <b>캐시 관리</b>
          <button className="modal-x" onClick={onClose} aria-label="닫기">
            ✕
          </button>
        </div>
        <div className="admin-body">
          <p className="auth-note">
            AI 호출당 모델 등급별 캐시 1~5가 차감됩니다 (flash 급 1 · sonnet 급 2 · gpt 급 3 · opus 급 5).
            관리자는 캐시 없이 사용 가능합니다.
          </p>
          {error && <div className="auth-error">{error}</div>}
          {notice && <div className="admin-notice">{notice}</div>}
          <table className="admin-table">
            <thead>
              <tr>
                <th>아이디</th>
                <th>역할</th>
                <th>캐시</th>
                <th>사용</th>
                <th>지급/차감</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.username}>
                  <td>{u.username}</td>
                  <td>{u.role === 'admin' ? '관리자' : '사용자'}</td>
                  <td className="num">{u.credits}</td>
                  <td className="num">{u.usedCalls}</td>
                  <td className="admin-actions">
                    <input
                      type="number"
                      min={1}
                      value={amounts[u.username] ?? '10'}
                      onChange={(e) => setAmounts((a) => ({ ...a, [u.username]: e.target.value }))}
                    />
                    <button disabled={busy === u.username} onClick={() => void grant(u.username, 1)}>
                      ＋지급
                    </button>
                    <button disabled={busy === u.username} onClick={() => void grant(u.username, -1)}>
                      －차감
                    </button>
                    <button
                      disabled={busy === u.username}
                      title="임시 비밀번호를 발급합니다"
                      onClick={() => void resetPassword(u.username)}
                    >
                      비번 리셋
                    </button>
                  </td>
                </tr>
              ))}
              {!users.length && !error && (
                <tr>
                  <td colSpan={5}>가입한 사용자가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>

          <div className="admin-invite-head">
            <b>초대코드</b>
            <span className="auth-note">가입에는 1회용 초대코드가 필요합니다.</span>
            <button className="auth-btn" disabled={busy === '__invite__'} onClick={() => void issueInvite()}>
              ＋발급
            </button>
          </div>
          <table className="admin-table">
            <thead>
              <tr>
                <th>코드</th>
                <th>상태</th>
                <th>발급일</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {invites.map((i) => (
                <tr key={i.code}>
                  <td className="invite-code">{i.code}</td>
                  <td>{i.usedBy ? `사용됨 (${i.usedBy})` : '미사용'}</td>
                  <td>{i.createdAt.slice(0, 10)}</td>
                  <td className="admin-actions">
                    {!i.usedBy && (
                      <button disabled={busy === i.code} onClick={() => void revoke(i.code)}>
                        폐기
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {!invites.length && (
                <tr>
                  <td colSpan={4}>발급된 초대코드가 없습니다. ＋발급을 눌러 만드세요.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
